/**
 * Which products may this visitor start a team from? (#2630)
 *
 * Signed out: the public `/api/products` list (only `public` products).
 * Signed in: `products_offered` on the account, which also counts the
 * `invite` state. While the answer is not known, only `shifts` counts as
 * offered, so a slow network never opens a product by mistake.
 *
 * A plain effect, not React Query: the question page renders before any
 * provider-heavy shell and must not fail when the API is unreachable.
 */
import { useEffect, useState } from "react";

import { useAuth } from "../lib/AuthContext";
import { INVITE_CHANGED_EVENT, inviteQuery } from "../lib/productInvite";
import { type ProductKey, productByKey } from "../lib/products";
import { apiFetch } from "./client";

type PublicOffers = {
  products: Array<{ key: string; offered: boolean }>;
  ask_question?: boolean;
};

/** Open products that have an answer card (the unlisted ones do not count). */
function countAnswers(keys: readonly string[]): number {
  return keys.filter((k) => productByKey(k)?.listed).length;
}

const FALLBACK: readonly string[] = ["shifts"];

/** The offer check, plus `askQuestion`: true when two or more products are open (#2654). */
export type ProductOffers = ((key: ProductKey) => boolean) & { askQuestion: boolean };

export function useProductOffers(): ProductOffers {
  const { account, accountId } = useAuth();
  const accountOffers = account?.products_offered;
  const [publicOffers, setPublicOffers] = useState<readonly string[]>(FALLBACK);
  const [publicAsk, setPublicAsk] = useState(false);
  // #2652 — the stored code or a redemption changed: read the offers again.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((n) => n + 1);
    window.addEventListener(INVITE_CHANGED_EVENT, bump);
    return () => window.removeEventListener(INVITE_CHANGED_EVENT, bump);
  }, []);

  // `tick` is a refetch trigger, not a value the effect reads.
  // biome-ignore lint/correctness/useExhaustiveDependencies: tick triggers the refetch
  useEffect(() => {
    if (accountId) return;
    let alive = true;
    apiFetch<PublicOffers>(`/api/products${inviteQuery()}`)
      .then((res) => {
        if (!alive) return;
        setPublicOffers(res.products.filter((p) => p.offered).map((p) => p.key));
        // An old API (deploy gap) sends no flag: count the answers instead.
        const open = res.products.filter((p) => p.offered).map((p) => p.key);
        setPublicAsk(res.ask_question ?? countAnswers(open) >= 2);
      })
      .catch(() => {
        /* keep the fallback */
      });
    return () => {
      alive = false;
    };
  }, [accountId, tick]);

  const offered = accountId ? (accountOffers ?? FALLBACK) : publicOffers;
  const askQuestion = accountId ? countAnswers(offered) >= 2 : publicAsk;
  return Object.assign((key: ProductKey) => offered.includes(key), { askQuestion });
}
